/**
 * useStackOperations - Stack mode operations for BufferSyncContext
 *
 * Extracted from BufferSyncContext.tsx. Manages the card stack:
 * addToStack, addDividerToStack, removeFromStack, insertAtIndex,
 * updateStackItem, updateDividerTitle, reorderStack, clearStack, setStackFromHydration.
 *
 * @module @humanizer/studio/hooks/useStackOperations
 */

import { useCallback, type Dispatch, type SetStateAction, type MutableRefObject } from 'react';
import type { ContentItem, StackItem } from '../contexts/buffer-sync-types';
import { cleanBufferName } from '../contexts/buffer-sync-utils';
import { createLogger } from '../utils/debug';

const log = createLogger('StackOps');

export interface StackOperationsDeps {
  isElectron: boolean;
  stackItems: StackItem[];
  setStackItems: Dispatch<SetStateAction<StackItem[]>>;
  activeStackIndex: number;
  setActiveStackIndex: Dispatch<SetStateAction<number>>;
  activeBufferIdRef: MutableRefObject<string | null>;
  workingContentRef: MutableRefObject<ContentItem[]>;
  pendingAddsRef: MutableRefObject<Set<string>>;
  stackSaveTimerRef: MutableRefObject<Map<string, ReturnType<typeof setTimeout>>>;
  reorderTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  stackRestoredRef: MutableRefObject<boolean>;
  // Project/stack context
  activeProjectId: string | null | undefined;
  activeStackId: string | null | undefined;
  addBufferToProject: (bufferId: string) => Promise<void>;
  removeBufferFromProject: (bufferId: string) => Promise<void>;
  ensureWorkingStack: () => Promise<{ id: string } | null>;
}

export interface StackOperationsReturn {
  addToStack: (item: ContentItem, bufferId?: string) => Promise<void>;
  addDividerToStack: (title: string) => Promise<void>;
  removeFromStack: (index: number) => void;
  insertAtIndex: (item: StackItem, index: number) => void;
  updateStackItem: (index: number, text: string) => void;
  updateDividerTitle: (index: number, title: string) => void;
  reorderStack: (fromIndex: number, toIndex: number) => void;
  clearStack: () => void;
  setStackFromHydration: (items: StackItem[]) => void;
}

export function useStackOperations(deps: StackOperationsDeps): StackOperationsReturn {
  const {
    isElectron,
    stackItems,
    setStackItems,
    setActiveStackIndex,
    activeBufferIdRef,
    pendingAddsRef,
    stackSaveTimerRef,
    reorderTimerRef,
    stackRestoredRef,
    activeProjectId,
    activeStackId,
    addBufferToProject,
    removeBufferFromProject,
    ensureWorkingStack,
  } = deps;

  const addToStack = useCallback(async (item: ContentItem, bufferId?: string) => {
    const summary = cleanBufferName(
      item.metadata.source?.threadTitle ??
      item.metadata.source?.path?.[0] ??
      item.text.split('\n')[0]?.trim() ??
      'Untitled',
      'Untitled'
    );
    const source = [
      item.metadata.source?.type,
      item.metadata.timestamp ? new Date(item.metadata.timestamp).toLocaleDateString() : null,
    ].filter(Boolean).join(' \u00b7 ');

    // Dedup key for concurrency guard
    const dedupKey = bufferId ?? item.metadata.source?.nodeId ?? item.id;

    // Check duplicates synchronously first
    const existingByBuffer = bufferId ? stackItems.find(s => s.bufferId === bufferId) : null;
    if (existingByBuffer) {
      const idx = stackItems.indexOf(existingByBuffer);
      if (idx >= 0) setActiveStackIndex(idx);
      return;
    }
    const existingByItem = stackItems.find(s => s.contentItem.id === item.id);
    if (existingByItem) {
      const idx = stackItems.indexOf(existingByItem);
      if (idx >= 0) setActiveStackIndex(idx);
      return;
    }
    const nodeId = item.metadata.source?.nodeId;
    if (nodeId) {
      const existingByNode = stackItems.find(s => s.sourceNodeId === nodeId);
      if (existingByNode) {
        const idx = stackItems.indexOf(existingByNode);
        if (idx >= 0) setActiveStackIndex(idx);
        return;
      }
    }

    // Concurrency guard: prevent duplicate buffer creation for same content
    if (pendingAddsRef.current.has(dedupKey)) return;
    pendingAddsRef.current.add(dedupKey);

    try {
      let resolvedBufferId = bufferId;

      // Create buffer if needed (Electron only — cloud uses existing API methods)
      if (!resolvedBufferId && isElectron && window.electronBuffer) {
        try {
          const saved = await window.electronBuffer.createFromText(item.text, {
            format: 'markdown',
            initialState: 'staged',
            sourcePlatform: item.metadata.source?.type,
            authorRole: item.metadata.authorRole,
            metadata: {
              nodeId: item.metadata.source?.nodeId,
              threadId: item.metadata.source?.threadId,
              threadTitle: item.metadata.source?.threadTitle,
            },
          });
          resolvedBufferId = saved.id;
        } catch (err) {
          log.warn('addToStack buffer creation failed:', err);
          // Card still appears — use item.id as fallback bufferId
          resolvedBufferId = item.id;
        }
      }

      if (!resolvedBufferId) {
        resolvedBufferId = item.id;
      }

      // Associate with named stack (or fall back to project-buffer)
      if (activeProjectId && isElectron && window.electronStack) {
        // Ensure we have an active stack (auto-creates "Working Stack")
        const stackId = activeStackId ?? (await ensureWorkingStack())?.id;
        if (stackId) {
          window.electronStack.addBuffer(stackId, resolvedBufferId).catch(() => {});
        }
      } else if (activeProjectId) {
        addBufferToProject(resolvedBufferId).catch(() => {});
      }

      const newItem: StackItem = {
        bufferId: resolvedBufferId,
        contentItem: item,
        summary,
        source,
        wordCount: item.metadata.wordCount ?? item.text.split(/\s+/).filter(Boolean).length,
        qualityScore: (item.metadata as Record<string, unknown>).excellenceScore as number | undefined,
        sourceNodeId: item.metadata.source?.nodeId,
        sortOrder: 0, // will be set below
      };

      setStackItems(prev => {
        // Final dedup check inside setter (state may have changed during await)
        if (prev.some(s => s.bufferId === resolvedBufferId)) return prev;
        if (nodeId && prev.some(s => s.sourceNodeId === nodeId)) return prev;
        newItem.sortOrder = prev.length;
        setActiveStackIndex(prev.length);
        return [...prev, newItem];
      });
    } finally {
      pendingAddsRef.current.delete(dedupKey);
    }
  }, [stackItems, isElectron, activeProjectId, activeStackId, addBufferToProject, ensureWorkingStack, setStackItems, setActiveStackIndex, pendingAddsRef]);

  const addDividerToStack = useCallback(async (title: string) => {
    let resolvedBufferId: string | undefined;

    // Create a buffer for the divider (Electron only)
    if (isElectron && window.electronBuffer) {
      try {
        const saved = await window.electronBuffer.createFromText('', {
          format: 'markdown',
          initialState: 'staged',
          metadata: { isDivider: true, dividerTitle: title },
        });
        resolvedBufferId = saved.id;
      } catch (err) {
        log.warn('addDividerToStack buffer creation failed:', err);
      }
    }

    if (!resolvedBufferId) {
      resolvedBufferId = crypto.randomUUID();
    }

    // Associate with named stack (or fall back to project-buffer)
    if (activeProjectId && isElectron && window.electronStack) {
      const stackId = activeStackId ?? (await ensureWorkingStack())?.id;
      if (stackId) {
        window.electronStack.addBuffer(stackId, resolvedBufferId).catch(() => {});
      }
    } else if (activeProjectId) {
      addBufferToProject(resolvedBufferId).catch(() => {});
    }

    const dividerItem: StackItem = {
      bufferId: resolvedBufferId,
      contentItem: {
        id: resolvedBufferId,
        type: 'text',
        text: '',
        metadata: {},
      },
      summary: title,
      source: '',
      wordCount: 0,
      sortOrder: 0,
      isDivider: true,
      dividerTitle: title,
    };

    setStackItems(prev => {
      dividerItem.sortOrder = prev.length;
      setActiveStackIndex(prev.length);
      return [...prev, dividerItem];
    });
  }, [isElectron, activeProjectId, activeStackId, addBufferToProject, ensureWorkingStack, setStackItems, setActiveStackIndex]);

  const removeFromStack = useCallback((index: number) => {
    // Capture the removed item's bufferId before state update
    const removed = stackItems[index];
    setStackItems(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((item, i) => ({ ...item, sortOrder: i }));
    });
    setActiveStackIndex(prev => Math.min(prev, Math.max(0, stackItems.length - 2)));

    // Remove from named stack (or fall back to project-buffer)
    if (removed?.bufferId && activeProjectId) {
      if (activeStackId && isElectron && window.electronStack) {
        window.electronStack.removeBuffer(activeStackId, removed.bufferId).catch(() => {});
      } else {
        removeBufferFromProject(removed.bufferId).catch(() => {});
      }
    }
  }, [stackItems, activeProjectId, activeStackId, isElectron, removeBufferFromProject, setStackItems, setActiveStackIndex]);

  const insertAtIndex = useCallback((item: StackItem, index: number) => {
    setStackItems(prev => {
      const clampedIndex = Math.min(Math.max(0, index), prev.length);
      const next = [...prev];
      next.splice(clampedIndex, 0, item);
      const reordered = next.map((s, i) => ({ ...s, sortOrder: i }));

      // Re-associate with named stack (or project)
      if (activeProjectId && item.bufferId) {
        if (activeStackId && isElectron && window.electronStack) {
          window.electronStack.addBuffer(activeStackId, item.bufferId, clampedIndex).catch(() => {});
        } else {
          addBufferToProject(item.bufferId).catch(() => {});
        }
      }

      setActiveStackIndex(clampedIndex);
      return reordered;
    });
  }, [activeProjectId, activeStackId, isElectron, addBufferToProject, setStackItems, setActiveStackIndex]);

  const updateDividerTitle = useCallback((index: number, title: string) => {
    let bufferId: string | undefined;
    setStackItems(prev => {
      if (index < 0 || index >= prev.length) return prev;
      const item = prev[index];
      if (!item.isDivider) return prev;
      bufferId = item.bufferId;
      const updated: StackItem = {
        ...item,
        summary: title,
        dividerTitle: title,
      };
      const next = [...prev];
      next[index] = updated;
      return next;
    });

    // Update buffer metadata on Electron
    if (bufferId && isElectron && window.electronBuffer) {
      const bid = bufferId;
      window.electronBuffer.load(bid).then(buffer => {
        if (buffer) {
          const meta = buffer.origin?.metadata ?? {};
          buffer.origin = {
            ...buffer.origin,
            metadata: { ...meta, dividerTitle: title },
          };
          window.electronBuffer!.save(buffer).catch(() => {});
        }
      }).catch(() => {});
    }
  }, [isElectron, setStackItems]);

  const updateStackItem = useCallback((index: number, text: string) => {
    let bufferId: string | undefined;
    setStackItems(prev => {
      if (index < 0 || index >= prev.length) return prev;
      const item = prev[index];
      bufferId = item.bufferId;
      const wordCount = text.split(/\s+/).filter(Boolean).length;
      const summary = cleanBufferName(
        text.split('\n')[0]?.trim() ?? 'Untitled',
        'Untitled'
      );
      const updated: StackItem = {
        ...item,
        contentItem: {
          ...item.contentItem,
          text,
          metadata: { ...item.contentItem.metadata, wordCount },
        },
        summary,
        wordCount,
      };
      const next = [...prev];
      next[index] = updated;
      return next;
    });

    // Debounced buffer save (5s, per-card)
    if (bufferId && isElectron && window.electronBuffer) {
      const existingTimer = stackSaveTimerRef.current.get(bufferId);
      if (existingTimer) clearTimeout(existingTimer);

      const bid = bufferId;
      stackSaveTimerRef.current.set(bid, setTimeout(async () => {
        stackSaveTimerRef.current.delete(bid);
        try {
          const buffer = await window.electronBuffer!.load(bid);
          if (buffer) {
            const wordCount = text.split(/\s+/).filter(Boolean).length;
            await window.electronBuffer!.save({ ...buffer, text, wordCount });
          }
        } catch (err) {
          log.warn('Stack card save failed:', err);
        }
      }, 5000));
    }
  }, [isElectron, setStackItems, stackSaveTimerRef]);

  const reorderStack = useCallback((fromIndex: number, toIndex: number) => {
    setStackItems(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      const reordered = next.map((item, i) => ({ ...item, sortOrder: i }));

      // Debounced sort order sync (1s)
      if (activeProjectId && isElectron) {
        if (reorderTimerRef.current) clearTimeout(reorderTimerRef.current);
        reorderTimerRef.current = setTimeout(() => {
          reorderTimerRef.current = null;
          const orderedIds = reordered.map(s => s.bufferId);
          if (activeStackId && window.electronStack) {
            window.electronStack.reorder(activeStackId, orderedIds).catch(() => {});
          } else if (window.electronHarvest?.reorderBuffers) {
            window.electronHarvest.reorderBuffers(activeProjectId!, orderedIds).catch(() => {});
          }
        }, 1000);
      }

      return reordered;
    });
  }, [activeProjectId, activeStackId, isElectron, setStackItems, reorderTimerRef]);

  const clearStack = useCallback(() => {
    // Remove all associations (fire-and-forget)
    if (activeProjectId) {
      for (const item of stackItems) {
        if (item.bufferId) {
          if (activeStackId && isElectron && window.electronStack) {
            window.electronStack.removeBuffer(activeStackId, item.bufferId).catch(() => {});
          } else {
            removeBufferFromProject(item.bufferId).catch(() => {});
          }
        }
      }
    }
    setStackItems([]);
    setActiveStackIndex(0);
    stackRestoredRef.current = false;
  }, [stackItems, activeProjectId, activeStackId, isElectron, removeBufferFromProject, setStackItems, setActiveStackIndex, stackRestoredRef]);

  const setStackFromHydration = useCallback((items: StackItem[]) => {
    stackRestoredRef.current = true;
    setStackItems(items);
    setActiveStackIndex(0);
  }, [setStackItems, setActiveStackIndex, stackRestoredRef]);

  return {
    addToStack,
    addDividerToStack,
    removeFromStack,
    insertAtIndex,
    updateStackItem,
    updateDividerTitle,
    reorderStack,
    clearStack,
    setStackFromHydration,
  };
}
